import BuscaDePessoas from 'components/ui/busca-de-pessoas';
import Paginacao from 'components/ui/paginacao';

import { corDoFunci, POR_PAGINA, rotuloDoResultado } from '../feed-calculos';
import AvatarDoFunci from './AvatarDoFunci';
import { FOCO_VISIVEL } from 'utils/foco';

export interface Funci {
  _id?: string;
  nome?: string;
  nome_guerra?: string;
  matricula: string;
  diretoria?: string;
  funcao?: string;
  avatar_type?: number;
  avatar?: unknown;
  image?: { medium?: { url?: string }; small?: { url?: string } };
}

interface FuncisParticipantesProps {
  funcis: Funci[];
  total?: number;
  pagina: number;
  carregando: boolean;
  buscaInicial: string;
  onBuscar: (termo: string) => void;
  onPagina: (pagina: number) => void;
  onAbrir: (funci: Funci) => void;
}

/** Título da seção; a página rola até ele ao trocar de página ou de busca. */
export const ID_DO_TITULO_DOS_FUNCIS = 'titulo-funcis';

// O anel de foco contorna o cartão inteiro, desenhado pela área estendida do botão.
const FOCO_DO_CARTAO =
  'focus-visible:outline-none focus-visible:after:outline focus-visible:after:outline-2 focus-visible:after:outline-offset-2 focus-visible:after:outline-brand-blue-dark';

export default function FuncisParticipantes({
  funcis,
  total,
  pagina,
  carregando,
  buscaInicial,
  onBuscar,
  onPagina,
  onAbrir,
}: FuncisParticipantesProps) {
  return (
    <section aria-labelledby={ID_DO_TITULO_DOS_FUNCIS} className="mt-7 flex flex-col rounded-[20px] bg-surface-100 px-[26px] pb-[26px] pt-6">
      <div className="mb-3.5 flex flex-wrap items-end justify-between gap-5">
        <div>
          <p id={`${ID_DO_TITULO_DOS_FUNCIS}-sobretitulo`} className="m-0 font-display text-[11px] font-bold uppercase tracking-[0.12em] text-brand-blue-dark">
            Quem joga junto
          </p>
          <h2
            id={ID_DO_TITULO_DOS_FUNCIS}
            tabIndex={0}
            aria-describedby={`${ID_DO_TITULO_DOS_FUNCIS}-sobretitulo ${ID_DO_TITULO_DOS_FUNCIS}-subtitulo`}
            className={`m-0 mb-1 mt-[5px] font-display text-[20px] font-bold leading-[1.2] text-brand-blue-dark ${FOCO_VISIVEL}`}
          >
            Funcis participantes
          </h2>
          <p id={`${ID_DO_TITULO_DOS_FUNCIS}-subtitulo`} className="m-0 text-[13px] text-content">
            Busque um colega para ver os reconhecimentos e as realizações dele no ciclo.
          </p>
        </div>
        {/* SPEC_DEVIATION: borda `content-soft` (design #E5E7EB, 1,24:1) para o limite do campo passar 3:1 contra o branco e o cinza.
            A busca filtra ao digitar e no Enter (AJ2-46). */}
        <BuscaDePessoas
          rotulo="Pesquisar no Feed Joga Junto"
          placeholder="Pesquise por nome, nome de guerra, matrícula ou prefixo"
          valorInicial={buscaInicial}
          onBuscar={onBuscar}
          classeDoFormulario="w-[420px] max-w-full"
          classeDoCampo="box-border w-full rounded-md border border-solid border-content-soft bg-white py-3 pl-4 pr-[42px] font-text text-[13px] text-content outline-none transition focus:border-brand-blue focus:shadow-[0_0_0_3px_rgba(70,94,255,0.14)]"
          depoisDoCampo={
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--brand-blue)"
              strokeWidth="2.3"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
              className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
          }
        />
      </div>

      {/* SPEC_DEVIATION: texto em `content` (design `text-soft`, 4,43:1) e número em `brand-blue-dark` (design `brand-blue`, 4,35:1) neste fundo. */}
      <p tabIndex={0} className={`m-0 mb-3 text-[11px] text-content ${FOCO_VISIVEL}`} aria-live="polite">
        {typeof total === 'number' && (
          <>
            <b className="font-bold text-brand-blue-dark">{total}</b> {rotuloDoResultado(total)}
          </>
        )}
      </p>

      {!carregando && funcis.length === 0 ? (
        <div tabIndex={0} className={`rounded-xl bg-brand-white p-[34px] text-center shadow-jj-01 ${FOCO_VISIVEL}`}>
          <p className="m-0 mb-1.5 font-display text-[15px] font-bold text-brand-blue-dark">Nenhum funci encontrado</p>
          <p className="m-0 text-[12px] text-content-soft">Revise o nome, o nome de guerra, a matrícula ou o prefixo pesquisado.</p>
        </div>
      ) : (
        <ul className="m-0 grid list-none grid-cols-1 gap-4 p-0 sm:grid-cols-2 lg:grid-cols-3" aria-busy={carregando}>
          {funcis.map((funci, indice) => (
            // Cartão inteiro clicável pelo botão "Ver mais" estendido (`after:inset-0`): o nome
            // acessível contém o texto visível (WCAG 2.5.3) e o leitor ouve o cartão como texto.
            <li
              key={funci.matricula}
              className="group relative flex flex-col rounded-xl bg-brand-white text-left shadow-jj-01 transition hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(0,45,75,0.12)] active:translate-y-0 active:shadow-jj-01 motion-reduce:hover:translate-y-0"
            >
              <div className="flex w-full justify-center px-[18px] pb-4 pt-[22px]">
                <AvatarDoFunci
                  nomeDeGuerra={funci.nome_guerra}
                  cor={corDoFunci(pagina * POR_PAGINA + indice)}
                  tamanho={88}
                  fonte="text-[26px]"
                  dados={funci}
                />
              </div>
              <div className="flex w-full flex-1 flex-col gap-[5px] rounded-b-xl bg-[#F6F7F9] px-[18px] pb-[18px] pt-4 text-center">
                <h3 id={`funci-${funci.matricula}`} className="m-0 font-display text-[13px] font-bold leading-[normal] text-content">
                  {funci.nome_guerra} · {funci.matricula}
                </h3>
                <p id={`funci-${funci.matricula}-nome`} className="m-0 text-[11px] leading-[1.4] text-content-soft">
                  {funci.nome}
                </p>
                <p id={`funci-${funci.matricula}-cargo`} className="m-0 text-[11px] leading-[1.4]">
                  <span className="font-semibold text-brand-blue">{funci.diretoria}</span>{' '}
                  <span className="text-content-soft">· {funci.funcao}</span>
                </p>
                <button
                  type="button"
                  onClick={() => onAbrir(funci)}
                  // Parada de Tab do cartão: o leitor lê o botão e, como descrição, o nome de guerra,
                  // a matrícula, o nome completo, a diretoria e a função.
                  aria-describedby={`funci-${funci.matricula} funci-${funci.matricula}-nome funci-${funci.matricula}-cargo`}
                  className={`mt-2 inline-flex cursor-pointer items-center justify-center gap-1.5 border-0 bg-transparent p-0 font-text text-[10px] font-bold uppercase leading-[normal] tracking-[0.04em] text-brand-blue transition-colors after:absolute after:inset-0 after:rounded-xl after:content-[''] group-hover:text-brand-blue-dark ${FOCO_DO_CARTAO}`}
                >
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    aria-hidden="true"
                    focusable="false"
                    className="transition-transform duration-200 group-hover:rotate-90 motion-reduce:group-hover:rotate-0"
                  >
                    <path d="M12 5v14" />
                    <path d="M5 12h14" />
                  </svg>
                  Ver mais<span className="sr-only"> sobre {funci.nome ?? funci.nome_guerra}</span>
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Paginacao total={Math.ceil((total ?? 0) / POR_PAGINA)} atual={pagina} onChange={onPagina} rotulo="Paginação dos funcis participantes" />
    </section>
  );
}
